import { ReactElement, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { TestTube, Trash, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Procedure } from "@/services/backend/procedures/procedure/procedure";
import {
  useDebounce,
  useDebouncedMutationWithPersistenceStateContextUpdate,
} from "@/shared/lib/debounce/debounce";
import { useListMouldsQuery } from "@/services/backend/mould/service";
import { FilterMode } from "@/shared/lib/filter/filter";
import {
  PutResultRequest,
  useDeleteResultMutation,
  useListResultsQuery,
  usePutResultMutation,
  usePutSampleSetFlagsMutation,
  useShowSampleSetFlagsQuery,
} from "@/services/backend/differentiation/service";
import { Mould } from "@/services/backend/mould/mould";
import { useGetSampleSetQuery } from "@/services/backend/sample-set/service";
import { DifferentiationResult } from "@/services/backend/differentiation/differentiation";
import { PersistenceStateUsingContext } from "@/shared/persistence-state/persistence-state-info";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { hasFieldError } from "@/shared/app-lib/errors/validation-error";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { Card } from "../../ui/card";
import { ScrollArea } from "../../ui/scroll-area";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { Field, FieldLabel } from "../../ui/field";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "../../ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import MouldResult from "../moulds/mould-result";
import { SampleSetNumber } from "../sample-set/sample-set-number";
import { checkIfThresholdIsExceeded } from "../moulds/mould-count";

const ESTIMATES = ["<10", "10-30", "31-50", "51-70", "71-100", ">100"];

function useSearchDebounce(
  value: string,
  delay?: number,
  minLength: number = 1,
): string {
  const [search, setSearch] = useState(value);

  if (value.length >= minLength && value !== search) {
    setSearch(value);
  }

  return useDebounce(search, delay);
}

interface DifferentiationDialogProps {
  sampleSetId: string;
  differentiationId: string;
  procedure: Procedure;
  disabled?: boolean;
}

export default function DifferentiationModal({
  sampleSetId,
  differentiationId,
  procedure,
  disabled = false,
}: DifferentiationDialogProps): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false);
  const { data: sampleSet } = useGetSampleSetQuery({ id: sampleSetId });

  if (sampleSet && !sampleSet.hasDifferentiation) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} variant="outline">
          <TestTube />
        </Button>
      </DialogTrigger>

      <DialogContent className="flex max-h-[90svh] min-w-full flex-col p-0 md:min-w-full lg:min-w-5xl">
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4">
          <DialogTitle className="flex flex-col gap-2 text-2xl font-bold">
            <span>Schimmelpilzdifferenzierung</span>
            <Badge>
              <div className="flex flex-row items-center gap-2">
                <span>Probensetnummer:</span>
                <SampleSetNumber sampleSetId={sampleSetId} />
              </div>
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Die vorhandenen Schimmelpilze für die weitere Zählung auswählen
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6">
          <MouldCountingForm
            differentiationId={differentiationId}
            sampleSetId={sampleSetId}
            procedure={procedure}
            disabled={disabled}
          />
        </div>

        <div className="bg-muted/30 flex flex-shrink-0 items-center justify-end border-t px-6 py-4">
          <PersistenceStateUsingContext />
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface MouldCountingFormProps {
  differentiationId: string;
  sampleSetId: string;
  procedure: Procedure;
  disabled: boolean;
}

function MouldCountingForm({
  differentiationId,
  sampleSetId,
  procedure,
  disabled,
}: MouldCountingFormProps): ReactElement {
  const [availableItems, setAvailableItems] = useState<Mould[]>([]);
  const [manualSelected, setManualSelected] = useState<DifferentiationResult[]>(
    [],
  );
  const [yeastResult, setYeastResult] = useState<DifferentiationResult | null>(
    null,
  );
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [exceedsThreshold, setExceedsThreshold] = useState(false);
  const [yeastExceedsThreshold, setYeastExceedsThreshold] = useState(false);
  const [search, setSearch] = useState("");
  const query = useSearchDebounce(search, 400, 0);

  const [putResult] = usePutResultMutation();
  const [putSampleSetFlags] = usePutSampleSetFlagsMutation();

  const { data: flags } = useShowSampleSetFlagsQuery({
    differentiationId,
    sampleSetId,
  });

  const { data: moulds } = useListMouldsQuery({
    description: {
      active: true,
      values: [`%${query}%`],
      mode: FilterMode.Like,
    },
  });

  const { data: results } = useListResultsQuery({
    sampleSetId: {
      active: true,
      values: [sampleSetId],
    },
    limit: 0,
    offset: 0,
  });

  useEffect(() => {
    if (!results?.items || !flags || resultsLoaded) return;

    setManualSelected(results.items.filter((result) => !result.mould?.isYeast));
    const currentYeast = results.items.find((result) => result.mould?.isYeast);
    if (currentYeast) {
      setYeastResult(currentYeast);
    }

    // The persisted per-sample-set flags are authoritative. Only when no flags
    // have been set yet AND no mould species were differentiated do we pre-fill
    // the mould checkbox from the counting's >100 measurement (matching the old
    // convenience behaviour).
    const noPersistedFlags = !flags.mouldExceeds && !flags.yeastExceeds;
    const mouldResultsEmpty =
      results.items.filter((r) => !r.mould?.isYeast).length === 0;
    const mouldThreshold = flags.mouldExceeds
      ? true
      : noPersistedFlags &&
        mouldResultsEmpty &&
        checkIfThresholdIsExceeded(procedure);

    setExceedsThreshold(Boolean(mouldThreshold));
    setYeastExceedsThreshold(flags.yeastExceeds);
    setResultsLoaded(true);
  }, [results, flags, resultsLoaded, procedure]);

  useEffect(() => {
    if (!moulds?.items) return;

    const selectedOriginalIds = new Set(
      manualSelected.map((r) => r.mould?.originalId),
    );

    setAvailableItems(
      moulds.items.filter(
        (m) => !selectedOriginalIds.has(m.originalId) && !m.isYeast,
      ),
    );

    // Wait until the saved results have been checked for an existing yeast
    // row before fabricating a placeholder - otherwise this can race ahead
    // of that check and mount a blank row that never picks up the real,
    // already-saved value once it arrives.
    if (!resultsLoaded) return;

    const yeastMould = moulds.items.find((m) => m.isYeast);
    if (yeastMould && !yeastResult) {
      setYeastResult({
        id: uuidv4(),
        sampleSetId,
        differentiationId,
        exceedsThreshold: yeastExceedsThreshold,
        overgrown: false,
        mould: { ...yeastMould, id: uuidv4() },
      });
    }
  }, [
    moulds,
    manualSelected,
    yeastResult,
    resultsLoaded,
    sampleSetId,
    differentiationId,
    yeastExceedsThreshold,
  ]);

  const onMouldExceedsChange = (checked: boolean) => {
    setExceedsThreshold(checked);
    putSampleSetFlags({
      differentiationId,
      sampleSetId,
      mouldExceeds: checked,
      yeastExceeds: yeastExceedsThreshold,
    });
  };

  const onYeastExceedsChange = (checked: boolean) => {
    setYeastExceedsThreshold(checked);
    putSampleSetFlags({
      differentiationId,
      sampleSetId,
      mouldExceeds: exceedsThreshold,
      yeastExceeds: checked,
    });
  };

  const onAvailableItemClick = (item: Mould) => {
    if (disabled) return;
    if (manualSelected.some((r) => r.mould?.originalId === item.originalId))
      return;

    const newResult: DifferentiationResult = {
      id: uuidv4(),
      sampleSetId,
      differentiationId,
      exceedsThreshold,
      overgrown: false,
      mould: { ...item, id: uuidv4() },
    };

    // Selecting a mould is itself the action that must be saved,
    // independent of whether a value is ever entered for it.
    putResult({ result: newResult });
    setManualSelected((prev) => [...prev, newResult]);
  };

  const onRowRemoved = (id: string) => {
    setManualSelected((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      <div className="flex flex-row space-x-4">
        <Field orientation="horizontal" className="w-fit flex-shrink-0">
          <Checkbox
            checked={exceedsThreshold}
            onCheckedChange={(checked) =>
              onMouldExceedsChange(Boolean(checked))
            }
            id="exceedsThreshold"
            disabled={disabled}
          />
          <FieldLabel htmlFor="exceedsThreshold">{">100 SP"}</FieldLabel>
        </Field>

        <Field orientation="horizontal" className="w-fit flex-shrink-0">
          <Checkbox
            checked={yeastExceedsThreshold}
            onCheckedChange={(checked) =>
              onYeastExceedsChange(Boolean(checked))
            }
            id="yeastExceedsThreshold"
            disabled={disabled}
          />
          <FieldLabel htmlFor="yeastExceedsThreshold">{">100 Hefe"}</FieldLabel>
        </Field>
      </div>

      <div className="flex min-h-0 flex-1 gap-6">
        <div className="flex min-h-0 w-[400px] flex-col">
          <div className="bg-muted flex-shrink-0 rounded-t-lg border-b px-4 py-3">
            <h3 className="font-semibold">
              Schimmelpilze ({availableItems.length})
            </h3>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <ScrollArea className="min-h-0 flex-1 rounded-b-lg border border-t-0">
              <div className="flex flex-col gap-2 p-3">
                {availableItems.map((item) => (
                  <Card
                    key={item.id}
                    onClick={() => onAvailableItemClick(item)}
                    className={cn(
                      "border-border p-3 transition-all",
                      disabled
                        ? "cursor-not-allowed opacity-50"
                        : "hover:border-primary/50 cursor-pointer",
                    )}
                  >
                    <div className="truncate text-sm font-medium">
                      {item.description}
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
            <Input
              placeholder="Suche..."
              onChange={(e) => setSearch(e.currentTarget.value)}
              value={search}
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="bg-muted flex-shrink-0 rounded-t-lg border-b px-4 py-3">
            <h3 className="font-semibold">
              Erkannte Schimmelpilze ({manualSelected.length})
            </h3>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <ScrollArea className="min-h-0 flex-1 rounded-b-lg border border-t-0">
              <div className="flex flex-col gap-3 p-3">
                {manualSelected.length === 0 ? (
                  <Empty>
                    <EmptyHeader>
                      <EmptyTitle>Keine Pilze ausgewählt</EmptyTitle>
                      <EmptyDescription>
                        Auf einen Schimmelpilz links klicken, um ihn
                        hinzuzufügen.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  manualSelected.map((item) => (
                    <MouldResultRow
                      key={item.id}
                      initialResult={item}
                      exceedsThreshold={exceedsThreshold}
                      disabled={disabled}
                      removable
                      onRemoved={onRowRemoved}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
            {yeastResult && (
              <MouldResultRow
                key={yeastResult.id}
                initialResult={yeastResult}
                exceedsThreshold={yeastExceedsThreshold}
                disabled={disabled}
                variant="inline"
              />
            )}
            <MouldResult className="w-full" procedure={procedure} />
          </div>
        </div>
      </div>
    </>
  );
}

interface MouldResultRowProps {
  initialResult: DifferentiationResult;
  exceedsThreshold: boolean;
  disabled?: boolean;
  removable?: boolean;
  onRemoved?: (id: string) => void;
  variant?: "card" | "inline";
}

function MouldResultRow({
  initialResult,
  exceedsThreshold,
  disabled = false,
  removable = false,
  onRemoved,
  variant = "card",
}: MouldResultRowProps): ReactElement {
  const { notReadyError, resetNotReadyErrorField } = useErrArtefactNotReady();
  const [request, setRequest] = useState<PutResultRequest>({
    result: initialResult,
  });
  const [putResult, { isLoading, isSuccess, error, reset }] =
    usePutResultMutation();
  useSetErrArtefactNotReadyFromRtk(error, () => reset());
  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    putResult,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  useEffect(() => {
    setRequest((prev) =>
      prev.result.exceedsThreshold === exceedsThreshold
        ? prev
        : { result: { ...prev.result, exceedsThreshold } },
    );
  }, [exceedsThreshold]);

  const [deleteResult] = useDeleteResultMutation();
  const { result } = request;

  const hasValueError = hasFieldError(
    notReadyError,
    `differentiationResult.${result.id}.value`,
  );

  const update = (updates: Partial<DifferentiationResult>) => {
    resetNotReadyErrorField(`differentiationResult.${result.id}.value`);
    setRequest((prev) => ({ result: { ...prev.result, ...updates } }));
  };

  const handleRemove = () => {
    deleteResult({ id: result.id });
    onRemoved?.(result.id);
  };

  const valueField = (
    <Field data-invalid={hasValueError} className="w-[120px] flex-none">
      <Input
        type="number"
        step={1}
        min={0}
        aria-invalid={hasValueError}
        disabled={disabled}
        defaultValue={result.value}
        onChange={(e) =>
          update({
            value: e.target.value !== "" ? Number(e.target.value) : undefined,
          })
        }
        placeholder="Zahl"
      />
    </Field>
  );

  const estimateField = (
    <div className="flex items-center gap-1">
      <Select
        value={result.estimate ?? ""}
        onValueChange={(value) => update({ estimate: value })}
        disabled={disabled}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Abschätzung" />
        </SelectTrigger>
        <SelectContent>
          {ESTIMATES.map((value) => (
            <SelectItem key={value} value={value}>
              {value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {result.estimate && !disabled && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => update({ estimate: undefined })}
        >
          <X />
        </Button>
      )}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="flex flex-shrink-0 flex-row items-center justify-end gap-2">
        <div className="truncate text-sm font-medium">
          {result.mould?.description}
        </div>
        {valueField}
        {estimateField}
      </div>
    );
  }

  return (
    <Card className="border-border p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="truncate text-sm font-medium">
            {result.mould?.description}
          </div>
          <div className="flex flex-row items-center gap-2">
            {valueField}
            {estimateField}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={result.overgrown ?? false}
                onCheckedChange={(checked) =>
                  update({ overgrown: Boolean(checked) })
                }
                id={`overgrown-${result.id}`}
                disabled={disabled}
              />
              <Label htmlFor={`overgrown-${result.id}`}>Überwachsen</Label>
            </div>
          </div>
        </div>
        {removable && !disabled && (
          <Button onClick={handleRemove} variant="destructive" size="icon">
            <Trash />
          </Button>
        )}
      </div>
    </Card>
  );
}
